import { Box, Flex, Image, Text, Divider, Tooltip } from "@chakra-ui/react";
import React, { useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { checkCollaboratorPermission } from "../pages/Users/utils/checkPermission";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  AtSign,
  Building2,
  ChartSpline,
  FileSearch2,
  FileText,
  Library,
  LucideIcon,
  Mail,
  ReceiptText,
  ShieldCheck,
  SquareKanban,
  Zap,
  Users,
  Layers,
  Menu,
  Settings,
} from "lucide-react";
import { LogoutModal } from "../pages/Users/modais/LogoutModal";
import { useAppData, useConfigGuard } from "../contexts/AppContext";
import { certificateHasJurisdiction } from "../utils/certificateJurisdiction";
import { subpaginasDoCliente } from "../pages/TaxCurrentAccount/contaCorrenteSlugs";
import { destinoDeAutomacoesNoMenu, getVisibleJurisdictions, loadedResources } from "../routes/routeAccess";
import { hasCredentialsModule } from "../utils/credentialsModule";

/**
 * Abaixo das camadas flutuantes (Radix/shadcn usam z-50, inclusive as do Kanban 2.0 e das Automações,
 * portalizadas no body) e acima do conteúdo da página. Com 99, o menu expandido cobria o assistente de
 * Automações (QA local GEN-3471, 02/10). Os modais Chakra (1400) já ficavam acima dos dois valores.
 */
const Z_SIDEBAR = 40;

// Re-export the contexts from their new home so the (~27) existing import sites
// that still reference "components/sidebar" keep working unchanged.
export { useClientContext, ClientContext, NotificationContext, ResourcesContext } from "../contexts/AppContext";

interface IMenuItemProps {
  onClick: any;
  img?: string;
  icon?: LucideIcon;
  label: string;
  small: boolean;
  selected: boolean;
  depth?: boolean;
  title?: string;
  isOpen?: boolean;
  style?: React.CSSProperties;
  ml?: string;
}

const MenuItem = (props: IMenuItemProps) => {
  const justifyContent = props.isOpen ? "flex-start" : "center";
  const Icon = props.icon;
  if (props.small) {
    if (props.selected) {
      return (
        <Tooltip label={props.label}>
          <Flex
            style={props.style}
            justifyContent={justifyContent}
            gap="8px"
            p="10px 25px"
            alignItems="center"
            backgroundColor="rgb(255,255,255,0.1)"
            _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
            onClick={props.onClick}
          >
            {Icon ? (
              <Icon style={{ width: "22px", height: "22px", color: "#ffffffd1" }} />
            ) : (
              <Image w="22px" h="22px" src={props.img} />
            )}
          </Flex>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip label={props.label}>
          <Flex
            style={props.style}
            justifyContent={justifyContent}
            gap="8px"
            p="10px 25px"
            alignItems="center"
            _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
            onClick={props.onClick}
          >
            {Icon ? (
              <Icon style={{ width: "22px", height: "22px", color: "#ffffffd1" }} />
            ) : (
              <Image w="22px" h="22px" src={props.img} />
            )}
          </Flex>
        </Tooltip>
      );
    }
  } else {
    if (props.selected) {
      return (
        <Flex
          style={props.style}
          justifyContent={justifyContent}
          gap="8px"
          marginLeft={props.depth ? (props.ml ? props.ml : "50px") : "0px"}
          p="10px 25px"
          alignItems="center"
          backgroundColor="rgb(255,255,255,0.1)"
          _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
          onClick={props.onClick}
        >
          {!props.depth ? (
            Icon ? (
              <Icon style={{ width: "22px", height: "22px", color: "#ffffffd1" }} />
            ) : (
              <Image w="22px" h="22px" src={props.img} />
            )
          ) : null}
          <Text fontSize="12px">{props.label}</Text>
        </Flex>
      );
    } else {
      return (
        <Flex
          style={props.style}
          justifyContent={justifyContent}
          gap="8px"
          marginLeft={props.depth ? (props.ml ? props.ml : "50px") : "0px"}
          p="10px 25px"
          alignItems="center"
          _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
          onClick={props.onClick}
        >
          {!props.depth ? (
            Icon ? (
              <Icon style={{ width: "22px", height: "22px", color: "#ffffffd1" }} />
            ) : (
              <Image w="22px" h="22px" src={props.img} />
            )
          ) : null}
          <Text fontSize="12px" title={props.title ? props.title : undefined}>
            {props.label}
          </Text>
        </Flex>
      );
    }
  }
};

interface ISidebarProps {
  /** GEN-3497: o estado vive no AppShell porque a navbar global também alterna a sidebar. */
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export default function Sidebar({ isOpen, setIsOpen }: ISidebarProps) {
  const appData = useAppData();
  const {
    validation,
    currentClient,
    collaborator,
    certificates,
    resources,
    resourcesLoaded,
    hasCpfProduct,
    hasCertificate,
    hasTaxCurrentAccountProduct,
  } = appData;
  // GEN-4080: Automações abre para quem tem o módulo (lista inteira) OU só alertas (aba Alertas) — a
  // mesma régua da rota (`podeAbrirAutomacoes`). `null` = item escondido — inclusive sem a liberação de
  // Automações para o cliente (GEN-4080 r4).
  const destinoDeAutomacoes = destinoDeAutomacoesNoMenu(appData);
  // GEN-4021: a mesma regra de jurisdição das rotas (routeAccess) — inclusive o fail-closed
  // enquanto os resources do cliente não carregaram: nenhum item por jurisdição aparece antes.
  const visibleJurisdictions = getVisibleJurisdictions(collaborator, loadedResources({ resources, resourcesLoaded }));
  const { requestNavigation } = useConfigGuard();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const [isExpanded, setIsExpanded] = useState(pathname === "/certidoes");
  const [isExpandedFederal, setIsExpandedFederal] = useState(false);
  const [isExpandedState, setIsExpandedState] = useState(false);
  const [isExpandedMunicipal, setIsExpandedMunicipal] = useState(false);
  // Taxly ISS (GEN-2972): submenu expansível (padrão do Gerenciador de Certidões)
  const [issExpanded, setIssExpanded] = useState(pathname.startsWith("/iss"));
  const [baseCadastralExpanded, setBaseCadastralExpanded] = useState<boolean>(pathname.startsWith("/base-cadastral"));
  // Conta Corrente (GEN-3984): submenu expansível com uma sub-página por consulta contratada
  const [contaCorrenteExpanded, setContaCorrenteExpanded] = useState<boolean>(pathname.startsWith("/conta-corrente"));

  const isActive = (path: string) => pathname === path;

  // --- Gerenciador de Certidões (URL-derived selection) ---
  const onCertidoes = pathname === "/certidoes";
  const currentType = searchParams.get("type") ?? "";
  const currentJurisdiction = searchParams.get("jurisdiction");

  const certPath = (type?: string, jurisdiction?: "federal" | "state" | "municipal" | null) => {
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    if (jurisdiction) params.set("jurisdiction", jurisdiction);
    const qs = params.toString();
    return qs ? `/certidoes?${qs}` : "/certidoes";
  };

  const certificatesMenu = () => {
    if (
      certificates.length > 0 &&
      checkCollaboratorPermission(validation, currentClient?.guid_client as string, "cnd.read")
    ) {
      const menuItems: JSX.Element[] = [];

      if (visibleJurisdictions.length === 0) {
        return <></>;
      }

      certificates.forEach((certificate, _key) => {
        if (
          (currentClient?.products[certificate.slug as keyof typeof currentClient.products] ||
            certificate.children?.some(
              (child) => currentClient?.products[child.slug as keyof typeof currentClient.products]
            ) ||
            (currentClient?.products_cpf &&
              (currentClient?.products_cpf[certificate.slug as keyof typeof currentClient.products] ||
                certificate.children?.some(
                  (child) => currentClient?.products_cpf[child.slug as keyof typeof currentClient.products]
                )))) &&
          ((collaborator?.userProducts &&
            (collaborator?.userProducts.includes(certificate.slug as string) ||
              certificate.children?.some((child) => collaborator?.userProducts?.includes(child.slug as string)))) ||
            collaborator?.permissions.includes("org.admin") ||
            collaborator?.permissions.includes("org.owner")) &&
          visibleJurisdictions.some((jurisdiction) => certificateHasJurisdiction(certificate, jurisdiction))
        ) {
          menuItems.push(
            <MenuItem
              isOpen={isOpen}
              selected={onCertidoes && currentType === certificate.slug}
              small={false}
              title={certificate.name as string}
              label={certificate.name as string}
              img="/icons/flow.svg"
              onClick={() => requestNavigation(certPath(certificate.slug as string))}
              depth={true}
            />
          );
        }
      });

      if (
        (collaborator?.userProducts === undefined ||
          collaborator?.userProducts?.length === 0 ||
          collaborator?.userProducts === null) &&
        !collaborator?.permissions.includes("org.admin") &&
        !collaborator?.permissions.includes("org.owner")
      ) {
        return <></>;
      }

      return (
        <>
          <Flex
            gap="8px"
            p="10px 25px"
            paddingRight="15px"
            alignItems="center"
            _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
            background={onCertidoes ? "rgb(255,255,255,0.1)" : "rgb(255,255,255,0)"}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <FileText width={22} height={22} />
            <Text fontSize="12px">
              <>
                Gerenciador de Certidões
                <ChevronDownIcon className={isExpanded ? "rotate" : "rotate-reverse"} w={4} h={4} />
              </>
            </Text>
          </Flex>

          {isExpanded ? <Flex flexDirection="column">{menuItems}</Flex> : null}
        </>
      );
    }
  };

  const certificatesByJurisdictionMenu = () => {
    if (
      certificates.length > 0 &&
      checkCollaboratorPermission(validation, currentClient?.guid_client as string, "cnd.read")
    ) {
      const menuItemsFederal: JSX.Element[] = [];
      const menuItemsState: JSX.Element[] = [];
      const menuItemsMunicipal: JSX.Element[] = [];
      let menuItemOutros: JSX.Element = <></>;

      if (visibleJurisdictions.length === 0) {
        return <></>;
      }

      certificates.forEach((certificate, _key) => {
        if (
          (currentClient?.products[certificate.slug as keyof typeof currentClient.products] ||
            certificate.children?.some((c) => currentClient?.products[c.slug as keyof typeof currentClient.products]) ||
            (currentClient?.products_cpf &&
              currentClient?.products_cpf[certificate.slug as keyof typeof currentClient.products]) ||
            certificate.children?.some(
              (c) => currentClient?.products_cpf[c.slug as keyof typeof currentClient.products]
            )) &&
          ((collaborator?.userProducts &&
            (collaborator?.userProducts.includes(certificate.slug as string) ||
              certificate.children?.some((c) => collaborator?.userProducts?.includes(c.slug as string)))) ||
            collaborator?.permissions.includes("org.admin") ||
            collaborator?.permissions.includes("org.owner"))
        ) {
          if (certificate.slug === "outros") {
            menuItemOutros = (
              <MenuItem
                isOpen={isOpen}
                selected={onCertidoes && currentType === certificate.slug}
                small={false}
                title="Outros"
                label="Outros"
                img="/icons/gerenciador_cnds_white.png"
                onClick={() => requestNavigation(certPath(certificate.slug as string))}
                depth={true}
                ml={currentClient?.certificate_menu === "jurisdiction" ? "25px" : "50px"}
              />
            );
          }

          if (
            certificateHasJurisdiction(certificate, "federal") &&
            certificate.slug !== "outros" &&
            visibleJurisdictions.includes("federal")
          ) {
            menuItemsFederal.push(
              <MenuItem
                isOpen={isOpen}
                selected={onCertidoes && currentType === certificate.slug && currentJurisdiction === "federal"}
                small={false}
                title={certificate.name as string}
                label={certificate.name as string}
                img="/icons/flow.svg"
                onClick={() => requestNavigation(certPath(certificate.slug as string, "federal"))}
                depth={true}
              />
            );
          }

          if (
            certificateHasJurisdiction(certificate, "state") &&
            certificate.slug !== "outros" &&
            visibleJurisdictions.includes("state")
          ) {
            menuItemsState.push(
              <MenuItem
                isOpen={isOpen}
                selected={onCertidoes && currentType === certificate.slug && currentJurisdiction === "state"}
                small={false}
                title={certificate.name as string}
                label={certificate.name as string}
                img="/icons/flow.svg"
                onClick={() => requestNavigation(certPath(certificate.slug as string, "state"))}
                depth={true}
              />
            );
          }

          if (
            certificateHasJurisdiction(certificate, "municipal") &&
            certificate.slug !== "outros" &&
            visibleJurisdictions.includes("municipal")
          ) {
            menuItemsMunicipal.push(
              <MenuItem
                isOpen={isOpen}
                selected={onCertidoes && currentType === certificate.slug && currentJurisdiction === "municipal"}
                small={false}
                title={certificate.name as string}
                label={certificate.name as string}
                img="/icons/flow.svg"
                onClick={() => requestNavigation(certPath(certificate.slug as string, "municipal"))}
                depth={true}
              />
            );
          }
        }
      });

      if (
        (collaborator?.userProducts === undefined ||
          collaborator?.userProducts?.length === 0 ||
          collaborator?.userProducts === null) &&
        !collaborator?.permissions.includes("org.admin") &&
        !collaborator?.permissions.includes("org.owner")
      ) {
        return <></>;
      }

      return (
        <>
          <Flex
            gap="8px"
            p="10px 25px"
            paddingRight="15px"
            alignItems="center"
            _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
            background={onCertidoes ? "rgb(255,255,255,0.1)" : "rgb(255,255,255,0)"}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <FileText width={22} height={22} />
            <Text fontSize="12px">
              <>
                Gerenciador de Certidões
                <ChevronDownIcon className={isExpanded ? "rotate" : "rotate-reverse"} w={4} h={4} />
              </>
            </Text>
          </Flex>

          {isExpanded ? (
            <>
              {menuItemsFederal.length > 0 ? (
                <>
                  <Flex
                    gap="8px"
                    p="10px 30px"
                    ml="25px"
                    alignItems="center"
                    _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                    cursor={!currentType && currentJurisdiction === "federal" ? "pointer" : "default"}
                    background={
                      !currentType && currentJurisdiction === "federal" && onCertidoes
                        ? "rgb(255,255,255,0.1)"
                        : "rgb(255,255,255,0)"
                    }
                    onClick={() => {
                      setIsExpandedFederal(!isExpandedFederal);
                      requestNavigation(certPath(undefined, "federal"));
                    }}
                  >
                    <Text fontSize="12px">
                      <>
                        Federal
                        <ChevronDownIcon className={isExpandedFederal ? "rotate" : "rotate-reverse"} w={4} h={4} />
                      </>
                    </Text>
                  </Flex>

                  {isExpandedFederal && <Flex flexDirection="column">{menuItemsFederal}</Flex>}
                </>
              ) : null}
              {menuItemsState.length > 0 ? (
                <>
                  <Flex
                    gap="8px"
                    p="10px 30px"
                    ml="25px"
                    alignItems="center"
                    _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                    cursor={!currentType && currentJurisdiction === "state" ? "pointer" : "default"}
                    background={
                      !currentType && currentJurisdiction === "state" && onCertidoes
                        ? "rgb(255,255,255,0.1)"
                        : "rgb(255,255,255,0)"
                    }
                    onClick={() => {
                      setIsExpandedState(!isExpandedState);
                      requestNavigation(certPath(undefined, "state"));
                    }}
                  >
                    <Text fontSize="12px">
                      <>
                        Estadual
                        <ChevronDownIcon className={isExpandedState ? "rotate" : "rotate-reverse"} w={4} h={4} />
                      </>
                    </Text>
                  </Flex>
                  {isExpandedState && <Flex flexDirection="column">{menuItemsState}</Flex>}
                </>
              ) : null}
              {menuItemsMunicipal.length > 0 ? (
                <>
                  <Flex
                    gap="8px"
                    p="10px 30px"
                    ml="25px"
                    alignItems="center"
                    _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                    cursor={!currentType && currentJurisdiction === "municipal" ? "pointer" : "default"}
                    background={
                      !currentType && currentJurisdiction === "municipal" && onCertidoes
                        ? "rgb(255,255,255,0.1)"
                        : "rgb(255,255,255,0)"
                    }
                    onClick={() => {
                      setIsExpandedMunicipal(!isExpandedMunicipal);
                      requestNavigation(certPath(undefined, "municipal"));
                    }}
                  >
                    <Text fontSize="12px">
                      <>
                        Municipal
                        <ChevronDownIcon className={isExpandedMunicipal ? "rotate" : "rotate-reverse"} w={4} h={4} />
                      </>
                    </Text>
                  </Flex>
                  {isExpandedMunicipal && <Flex flexDirection="column">{menuItemsMunicipal}</Flex>}
                </>
              ) : null}

              {menuItemOutros}
            </>
          ) : null}
        </>
      );
    }
  };

  // GEN-3984: as sub-páginas que o usuário vê (o CADIN Federal exige a jurisdição federal)
  const subpaginasContaCorrente = subpaginasDoCliente(
    currentClient?.products as Record<string, unknown> | undefined,
    visibleJurisdictions
  );

  const canShowTaxCurrentAccount = () => {
    if (
      hasTaxCurrentAccountProduct &&
      subpaginasContaCorrente.length > 0 &&
      checkCollaboratorPermission(validation, currentClient?.guid_client as string, "taxCurrentAccount.read")
    ) {
      return visibleJurisdictions.length > 0;
    }

    return false;
  };

  // GEN-3984 — "Conta Corrente" é módulo: Situação Fiscal e CADIN Federal, cada uma só para o cliente
  // que contratou a consulta dela (mesmo padrão de seta do Taxly ISS e da Base Cadastral).
  const taxCurrentAccountMenu = () => {
    if (canShowTaxCurrentAccount()) {
      const subpaginas = subpaginasContaCorrente;
      return (
        <>
          <Flex
            gap="8px"
            p="10px 25px"
            paddingRight="15px"
            alignItems="center"
            _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
            background={pathname.startsWith("/conta-corrente") ? "rgb(255,255,255,0.1)" : "rgb(255,255,255,0)"}
            onClick={() => setContaCorrenteExpanded(!contaCorrenteExpanded)}
          >
            <FileSearch2 width={22} height={22} />
            <Text fontSize="12px">
              <>
                Conta Corrente
                <ChevronDownIcon className={contaCorrenteExpanded ? "rotate" : "rotate-reverse"} w={4} h={4} />
              </>
            </Text>
          </Flex>
          {contaCorrenteExpanded ? (
            <Flex flexDirection="column">
              {subpaginas.map((sub) => (
                <MenuItem
                  key={sub.path}
                  isOpen={isOpen}
                  selected={pathname.startsWith(`/conta-corrente/${sub.path}`)}
                  small={false}
                  title={sub.label}
                  label={sub.label}
                  img="/icons/flow.svg"
                  depth={true}
                  onClick={() => requestNavigation(`/conta-corrente/${sub.path}`)}
                />
              ))}
            </Flex>
          ) : null}
        </>
      );
    }
  };

  const collaboratorHasAccessAnyJurisdiction = (): boolean => visibleJurisdictions.length > 0;

  return (
    <Flex
      className="sidebar"
      w={isOpen ? "260px" : "100px"}
      position="relative"
      fontFamily="Comfortaa"
      fontWeight="500"
    >
      {isOpen ? (
        <Flex
          w="260px"
          bg="#4B4EFF"
          position="relative"
          color="white"
          justifyContent="center"
          transition="300ms"
          zIndex={Z_SIDEBAR}
          left="0px"
          right="0px"
          top="0px"
          bottom="0px"
        >
          <Flex direction="column" h="100%" w="100%">
            {/* GEN-3497: logo e botão de recolher continuam AQUI, como sempre foram. A barra
                global vive sobre a área de conteúdo e não toma o topo da sidebar — tirá-los
                daqui partia a coluna azul ao meio. O que saiu foi só o seletor de cliente,
                que virou o primeiro degrau da trilha. */}
            <Flex direction="column" flexShrink={0}>
              <Flex
                justifyContent="end"
                transition="600ms"
                marginTop="20px"
                mt={2}
                onClick={() => setIsOpen(!isOpen)}
                _hover={{ cursor: "pointer" }}
              >
                <Flex h={35} w={35} borderRadius="100%" justifyContent="center" alignItems="center">
                  <Image src="/icons/arrow-close.svg" h={13} w={13} />
                </Flex>
              </Flex>
              <Flex justifyContent="center">
                <Text
                  className="logo"
                  color="white"
                  fontSize="32px"
                  cursor="pointer"
                  onClick={() => requestNavigation("/")}
                >
                  taxly
                </Text>
              </Flex>
            </Flex>

            <Flex
              pt={6}
              direction="column"
              className="scrollbarsidebar"
              overflowY="auto"
              flex={1}
              width="100%"
              paddingRight="2px"
              pb={4}
            >
              {/* GEN-3497 (r9): o grupo "Minha Taxly" (Meu histórico e Minhas tarefas) saiu daqui e
                  foi para o menu do avatar. Os dois são do USUÁRIO, não do cliente; a sidebar fica
                  com navegação entre produtos. */}

              <Flex direction={"column"} gap={2} mb={6}>
                <Text px={"14px"} fontSize={12} mb={2}>
                  Produtos
                </Text>
                <MenuItem
                  isOpen={isOpen}
                  selected={isActive("/")}
                  small={false}
                  title="Dashboard"
                  label="Dashboard"
                  icon={ChartSpline}
                  onClick={() => requestNavigation("/")}
                />
                {currentClient?.products?.kanban &&
                checkCollaboratorPermission(validation, currentClient.guid_client, "kanban.read") ? (
                  <MenuItem
                    isOpen={isOpen}
                    selected={isActive("/kanban")}
                    small={false}
                    title="Tax Kanban"
                    label="Tax Kanban"
                    icon={SquareKanban}
                    onClick={() => requestNavigation("/kanban")}
                  />
                ) : null}
                {/* Kanban 2.0 — rollout gradual: só clientes com product kanban2 (flag provisória, decisão E1).
                    GEN-3121: gate por kanban2.read (não mais kanban.read legado) — K2 e Kanban de Obrigações separados. */}
                {(currentClient?.products as Record<string, boolean> | undefined)?.kanban2 &&
                checkCollaboratorPermission(validation, currentClient?.guid_client as string, "kanban2.read") ? (
                  <MenuItem
                    isOpen={isOpen}
                    selected={isActive("/kanban-v2")}
                    small={false}
                    title="Kanban 2.0"
                    label="Kanban 2.0"
                    icon={SquareKanban}
                    onClick={() => requestNavigation("/kanban-v2/quadros")}
                  />
                ) : null}
                {/* Automações — módulo PRÓPRIO. O motor deixou de ser do Kanban (ADR-0002 do backend):
                    reage a fatos da Caixa Postal, de certidões, de Conta Corrente, de dívida ativa e da
                    situação cadastral. Enquanto a tela só existia dentro do Kanban, configurar uma regra
                    de Caixa Postal obrigava o usuário a entrar no quadro.
                    Gate: `destinoDeAutomacoesNoMenu` (routeAccess) — o módulo (produto + automations.read)
                    abre a lista inteira; só `alerts.read` abre a aba Alertas (GEN-4080). */}
                {destinoDeAutomacoes ? (
                  <MenuItem
                    isOpen={isOpen}
                    selected={isActive("/automacoes")}
                    small={false}
                    title="Automações"
                    label="Automações"
                    icon={Zap}
                    onClick={() => requestNavigation(destinoDeAutomacoes)}
                  />
                ) : null}
                {/* Taxly ISS (GEN-2972) — piloto Statkraft: só clientes com product iss.
                    V1: submenu expansível (Lotes/Apuração/Configurações) no padrão do
                    Gerenciador de Certidões — o dashboard de apuração não tinha entrada na UI. */}
                {(currentClient?.products as Record<string, boolean> | undefined)?.iss &&
                checkCollaboratorPermission(validation, currentClient?.guid_client as string, "iss.read") ? (
                  <>
                    <Flex
                      gap="8px"
                      p="10px 25px"
                      paddingRight="15px"
                      alignItems="center"
                      _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                      background={pathname.startsWith("/iss") ? "rgb(255,255,255,0.1)" : "rgb(255,255,255,0)"}
                      onClick={() => setIssExpanded(!issExpanded)}
                    >
                      <ReceiptText width={22} height={22} />
                      <Text fontSize="12px">
                        <>
                          Taxly ISS
                          <ChevronDownIcon className={issExpanded ? "rotate" : "rotate-reverse"} w={4} h={4} />
                        </>
                      </Text>
                    </Flex>
                    {issExpanded ? (
                      <Flex flexDirection="column">
                        <MenuItem
                          isOpen={isOpen}
                          selected={pathname.startsWith("/iss/lotes")}
                          small={false}
                          title="Lotes"
                          label="Lotes"
                          img="/icons/flow.svg"
                          depth={true}
                          onClick={() => requestNavigation("/iss/lotes")}
                        />
                        <MenuItem
                          isOpen={isOpen}
                          selected={isActive("/iss/dashboard")}
                          small={false}
                          title="Dashboard"
                          label="Dashboard"
                          img="/icons/flow.svg"
                          depth={true}
                          onClick={() => requestNavigation("/iss/dashboard")}
                        />
                        <MenuItem
                          isOpen={isOpen}
                          selected={isActive("/iss/config")}
                          small={false}
                          title="Configurações"
                          label="Configurações"
                          img="/icons/flow.svg"
                          depth={true}
                          onClick={() => requestNavigation("/iss/config")}
                        />
                      </Flex>
                    ) : null}
                  </>
                ) : null}

                {/* Base Cadastral (GEN-3162) — é PRODUTO, não configuração: mora aqui junto dos
                    outros, com submenu expansível no mesmo padrão do Gerenciador de Certidões e do
                    Taxly ISS. Antes ficava lá embaixo em Configurações e sem as subpáginas, então
                    as seis telas internas do módulo não tinham entrada nenhuma na navegação.
                    Condição idêntica ao canAccessBaseCadastral (routes/routeAccess.ts). */}
                {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "registry.read") &&
                (currentClient?.products as Record<string, boolean> | undefined)?.registry ? (
                  <>
                    <Flex
                      gap="8px"
                      p="10px 25px"
                      paddingRight="15px"
                      alignItems="center"
                      _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                      background={pathname.startsWith("/base-cadastral") ? "rgb(255,255,255,0.1)" : "rgb(255,255,255,0)"}
                      /* GEN-4016 — o nome do produto leva à Visão Geral do módulo (e abre o submenu).
                         Antes o clique só abria/fechava a lista e a home do módulo ficava sem entrada:
                         voltar a ela exigia o breadcrumb. Abrir/fechar sem navegar continua na seta. */
                      onClick={() => {
                        setBaseCadastralExpanded(true);
                        requestNavigation("/base-cadastral");
                      }}
                    >
                      <Library width={22} height={22} />
                      <Text fontSize="12px">
                        <>
                          Base Cadastral
                          {/* A seta agora é o único jeito de recolher: padding com margem negativa
                              amplia o alvo do clique sem mexer no layout, e o papel de botão a deixa
                              alcançável pelo teclado. */}
                          <Box
                            as="span"
                            role="button"
                            tabIndex={0}
                            display="inline-flex"
                            verticalAlign="middle"
                            p="4px"
                            m="-4px"
                            aria-label={baseCadastralExpanded ? "Recolher Base Cadastral" : "Expandir Base Cadastral"}
                            aria-expanded={baseCadastralExpanded}
                            data-testid="base-cadastral-toggle"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setBaseCadastralExpanded(!baseCadastralExpanded);
                            }}
                            onKeyDown={(e: React.KeyboardEvent) => {
                              if (e.key !== "Enter" && e.key !== " ") return;
                              e.preventDefault();
                              e.stopPropagation();
                              setBaseCadastralExpanded(!baseCadastralExpanded);
                            }}
                          >
                            <ChevronDownIcon
                              className={baseCadastralExpanded ? "rotate" : "rotate-reverse"}
                              w={4}
                              h={4}
                            />
                          </Box>
                        </>
                      </Text>
                    </Flex>
                    {baseCadastralExpanded ? (
                      <Flex flexDirection="column">
                        {[
                          // Igualdade exata (ver `selected` abaixo): toda rota do módulo começa com
                          // `/base-cadastral/`, então o prefixo acenderia a Visão Geral em todas.
                          { to: "/base-cadastral", label: "Visão Geral", exact: true },
                          { to: "/base-cadastral/empresa", label: "Por Empresa" },
                          { to: "/base-cadastral/grupos", label: "Grupo" },
                          { to: "/base-cadastral/pessoas", label: "Pessoas Vinculadas" },
                          { to: "/base-cadastral/incorporadas", label: "Incorporadas" },
                          { to: "/base-cadastral/credenciais", label: "Credenciais" },
                          { to: "/base-cadastral/documentos", label: "Documentos" },
                          { to: "/base-cadastral/dashboard", label: "Dashboard" },
                        ].map((sub) => (
                          <MenuItem
                            key={sub.to}
                            isOpen={isOpen}
                            /* Prefixo, não igualdade: "Por Empresa" e "Grupo" têm rota-filha
                               (`/empresa/:guid`, `/grupos/:guid`) e, com `===`, o subitem de onde a
                               pessoa acabou de clicar perdia o destaque ao abrir o detalhe. É o
                               mesmo tratamento que "Lotes" do ISS já recebe logo acima. */
                            selected={pathname === sub.to || (!sub.exact && pathname.startsWith(`${sub.to}/`))}
                            small={false}
                            title={sub.label}
                            label={sub.label}
                            img="/icons/flow.svg"
                            depth={true}
                            onClick={() => requestNavigation(sub.to)}
                          />
                        ))}
                      </Flex>
                    ) : null}
                  </>
                ) : null}

                {checkCollaboratorPermission(
                  validation,
                  currentClient?.guid_client as string,
                  "batchCertificatesOnDemand.read"
                ) &&
                  currentClient?.products.batch_certificate_on_demand && (
                    <MenuItem
                      isOpen={isOpen}
                      selected={isActive("/consultas-em-lote")}
                      small={false}
                      title={`Consultas em Lote`}
                      label={`Consultas em Lote`}
                      icon={Layers}
                      onClick={() => requestNavigation("/consultas-em-lote")}
                    />
                  )}

                {hasCertificate && currentClient?.certificate_menu !== "jurisdiction" ? certificatesMenu() : null}
                {hasCertificate && currentClient?.certificate_menu === "jurisdiction"
                  ? certificatesByJurisdictionMenu()
                  : null}

                {isExpanded && collaboratorHasAccessAnyJurisdiction() ? (
                  <MenuItem
                    isOpen={isOpen}
                    selected={onCertidoes && !currentType && !currentJurisdiction}
                    small={false}
                    title="Visão Geral"
                    label="Visão Geral"
                    img="/icons/gerenciador_cnds_white.png"
                    onClick={() => requestNavigation("/certidoes")}
                    depth={true}
                    ml={currentClient?.certificate_menu === "jurisdiction" ? "25px" : "50px"}
                  />
                ) : null}

                {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "dte.read") &&
                (currentClient?.products.dte_federal ||
                  currentClient?.products.dte_state ||
                  currentClient?.products.dte_municipal) &&
                collaboratorHasAccessAnyJurisdiction() ? (
                  <MenuItem
                    isOpen={isOpen}
                    selected={isActive("/caixa-postal")}
                    small={false}
                    title="Caixa Postal"
                    label="Caixa Postal"
                    icon={Mail}
                    onClick={() => requestNavigation("/caixa-postal")}
                  />
                ) : null}

                {taxCurrentAccountMenu()}
              </Flex>

              <Flex direction={"column"} gap={2}>
                <Text px={"14px"} fontSize={12}>
                  Configurações
                </Text>

                {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "company.read") ? (
                  <MenuItem
                    isOpen={isOpen}
                    selected={isActive("/empresas")}
                    small={false}
                    title="Empresas"
                    label="Empresas"
                    icon={Building2}
                    onClick={() => requestNavigation("/empresas")}
                  />
                ) : null}

                {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "persona.read") &&
                hasCpfProduct ? (
                  <MenuItem
                    isOpen={isOpen}
                    selected={isActive("/pessoa-fisica")}
                    small={false}
                    title="Pessoas Físicas"
                    label="Pessoas Físicas"
                    icon={Users}
                    onClick={() => requestNavigation("/pessoa-fisica")}
                  />
                ) : null}

                {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "user.read") ? (
                  <MenuItem
                    isOpen={isOpen}
                    selected={isActive("/usuarios")}
                    small={false}
                    title="Usuários"
                    label="Usuários"
                    icon={AtSign}
                    onClick={() => requestNavigation("/usuarios")}
                  />
                ) : null}

                {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "credential.read") &&
                hasCredentialsModule(currentClient, hasTaxCurrentAccountProduct) ? (
                  <MenuItem
                    isOpen={isOpen}
                    selected={isActive("/credenciais")}
                    small={false}
                    title="Credenciais"
                    label="Credenciais"
                    icon={ShieldCheck}
                    onClick={() => requestNavigation("/credenciais")}
                  />
                ) : null}

                {/* GEN-3497 (r7): Configurações VOLTA para o menu lateral. Ela tinha saído porque
                    o menu do avatar passou a levar até lá — mas isso deixava um caminho só, e
                    escondido atrás de um ícone que o usuário está aprendendo a usar agora. Menu e
                    barra podem levar ao mesmo lugar; o que não pode é a única porta ser a nova. */}
                {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "config.read") ? (
                  <MenuItem
                    isOpen={isOpen}
                    // GEN-3670 (r8): as seções têm caminho próprio (`/configuracoes/base-cadastral`).
                    selected={pathname.startsWith("/configuracoes")}
                    small={false}
                    title="Configurações"
                    label="Configurações"
                    icon={Settings}
                    onClick={() => requestNavigation("/configuracoes")}
                  />
                ) : null}
              </Flex>
            </Flex>

            {/* Sair continua aqui, além do menu do avatar: é redundante de propósito — sair é a
                ação que o usuário mais procura sob pressão, e ter duas portas não atrapalha. */}
            <Flex direction="column" gap={4} alignSelf="center" mt={4} mb={6} flexShrink={0}>
              {/* `alignSelf="center"` + divisor de 200px: é assim que o rodapé sempre foi alinhado
                  (mesma borda esquerda dos itens do menu). Com `w="100%"` o "Sair" encostava na
                  borda da sidebar, desalinhado de tudo. */}
              <Divider w="200px" />
              <LogoutModal st="complete" />
            </Flex>
          </Flex>
        </Flex>
      ) : (
        <Flex
          w="100px"
          bg="#4B4EFF"
          color="white"
          position="relative"
          zIndex={Z_SIDEBAR}
          left="0px"
          right="0px"
          top="0px"
          bottom="0px"
        >
          <Flex direction="column" w="100%" h="100%">
            <Flex direction="column" w="100%" flexShrink={0}>
              <Flex
                justifyContent="end"
                mt="10px"
                transition="300ms"
                onClick={() => setIsOpen(!isOpen)}
                _hover={{ cursor: "pointer" }}
              >
                <Flex h={35} w={32} borderRadius="100%" justifyContent="center" alignItems="center">
                  <Menu width="18px" />
                </Flex>
              </Flex>
              <Flex justifyContent="center">
                <Text color="white" fontSize="32px" cursor="pointer" onClick={() => requestNavigation("/")}>
                  t
                </Text>
              </Flex>
            </Flex>

            <Flex direction="column" w="100%" mt={8} flex={1} overflowY="auto" className="scrollbarsidebar" pb={4}>
              <MenuItem
                isOpen={isOpen}
                selected={isActive("/")}
                small={true}
                title="Dashboard"
                label="Dashboard"
                icon={ChartSpline}
                onClick={() => requestNavigation("/")}
              />
              {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "kanban.read") &&
              currentClient?.products.kanban ? (
                <MenuItem
                  isOpen={isOpen}
                  selected={isActive("/kanban")}
                  small={true}
                  title="Tax Kanban"
                  label="Tax Kanban"
                  icon={SquareKanban}
                  onClick={() => requestNavigation("/kanban")}
                />
              ) : null}
              {(currentClient?.products as Record<string, boolean> | undefined)?.kanban2 &&
              checkCollaboratorPermission(validation, currentClient?.guid_client as string, "kanban.read") ? (
                <MenuItem
                  isOpen={isOpen}
                  selected={isActive("/kanban-v2")}
                  small={true}
                  title="Kanban 2.0"
                  label="Kanban 2.0"
                  icon={SquareKanban}
                  onClick={() => requestNavigation("/kanban-v2/quadros")}
                />
              ) : null}
              {/* Automações — ver a nota na barra expandida. Duplicado porque a sidebar não tem
                  renderer compartilhado entre expandida e colapsada. */}
              {destinoDeAutomacoes ? (
                <MenuItem
                  isOpen={isOpen}
                  selected={isActive("/automacoes")}
                  small={true}
                  title="Automações"
                  label="Automações"
                  icon={Zap}
                  onClick={() => requestNavigation(destinoDeAutomacoes)}
                />
              ) : null}
              {(currentClient?.products as Record<string, boolean> | undefined)?.iss &&
              checkCollaboratorPermission(validation, currentClient?.guid_client as string, "iss.read") ? (
                <MenuItem
                  isOpen={isOpen}
                  selected={isActive("/iss")}
                  small={true}
                  title="Taxly ISS"
                  label="Taxly ISS"
                  icon={ReceiptText}
                  onClick={() => requestNavigation("/iss/lotes")}
                />
              ) : null}

              {/* Com a sidebar recolhida o módulo não tinha entrada nenhuma: era preciso reabrir a
                  sidebar inteira para chegar até ele. Mesmo gate e mesmo destino do bloco expandido. */}
              {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "registry.read") &&
              (currentClient?.products as Record<string, boolean> | undefined)?.registry ? (
                <MenuItem
                  isOpen={isOpen}
                  selected={pathname.startsWith("/base-cadastral")}
                  small={true}
                  title="Base Cadastral"
                  label="Base Cadastral"
                  icon={Library}
                  onClick={() => requestNavigation("/base-cadastral")}
                />
              ) : null}

              {checkCollaboratorPermission(
                validation,
                currentClient?.guid_client as string,
                "batchCertificatesOnDemand.read"
              ) &&
                currentClient?.products.batch_certificate_on_demand && (
                  <MenuItem
                    isOpen={isOpen}
                    selected={isActive("/consultas-em-lote")}
                    small={true}
                    title={`Consultas em Lote`}
                    label={`Consultas em Lote`}
                    icon={Layers}
                    onClick={() => requestNavigation("/consultas-em-lote")}
                  />
                )}

              {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "cnd.read") &&
              hasCertificate &&
              collaboratorHasAccessAnyJurisdiction() ? (
                <Tooltip label="Gerenciador de Certidões">
                  <Flex
                    justifyContent="center"
                    gap="8px"
                    p="10px 30px"
                    alignItems="center"
                    _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                    onClick={() => {
                      setIsOpen(true);
                      setIsExpanded(true);
                    }}
                  >
                    <FileText width={22} height={22} />
                  </Flex>
                </Tooltip>
              ) : null}

              {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "dte.read") &&
              (currentClient?.products.dte_federal ||
                currentClient?.products.dte_state ||
                currentClient?.products.dte_municipal) &&
              collaboratorHasAccessAnyJurisdiction() ? (
                <Tooltip label="Caixa Postal">
                  <Flex
                    justifyContent="center"
                    gap="8px"
                    p="10px 30px"
                    alignItems="center"
                    _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                    onClick={() => requestNavigation("/caixa-postal")}
                  >
                    <Mail color="#ffffffd1" width="22px" height="22px" />
                  </Flex>
                </Tooltip>
              ) : null}

              {canShowTaxCurrentAccount() ? (
                <Tooltip label="Conta Corrente">
                  <Flex
                    justifyContent="center"
                    gap="8px"
                    p="10px 30px"
                    alignItems="center"
                    _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                    onClick={() => requestNavigation("/conta-corrente")}
                  >
                    <FileSearch2 width="22px" height="22px" color="#ffffffd1" />
                  </Flex>
                </Tooltip>
              ) : null}

              {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "company.read") ? (
                <Tooltip label="Empresas">
                  <Flex
                    justifyContent="center"
                    gap="8px"
                    p="10px 30px"
                    alignItems="center"
                    _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                    onClick={() => requestNavigation("/empresas")}
                  >
                    <Building2 color="#ffffffd1" width="22px" height="22px" />
                  </Flex>
                </Tooltip>
              ) : null}

              {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "persona.read") &&
              hasCpfProduct ? (
                <Tooltip label="Pessoas Físicas">
                  <Flex
                    justifyContent="center"
                    gap="8px"
                    p="10px 30px"
                    alignItems="center"
                    _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                    onClick={() => requestNavigation("/pessoa-fisica")}
                  >
                    <Users color="#ffffffd1" width="22px" height="22px" />
                  </Flex>
                </Tooltip>
              ) : null}

              {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "user.read") ? (
                <Tooltip label="Usuários">
                  <Flex
                    justifyContent="center"
                    gap="8px"
                    p="10px 30px"
                    alignItems="center"
                    _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                    onClick={() => requestNavigation("/usuarios")}
                  >
                    <AtSign color="#ffffffd1" width="22px" height="22px" />
                  </Flex>
                </Tooltip>
              ) : null}

              {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "credential.read") &&
              hasCredentialsModule(currentClient, hasTaxCurrentAccountProduct) ? (
                <Tooltip label="Credenciais">
                  <Flex
                    justifyContent="center"
                    gap="8px"
                    p="10px 30px"
                    alignItems="center"
                    _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                    onClick={() => requestNavigation("/credenciais")}
                  >
                    <ShieldCheck color="#ffffffd1" width="22px" height="22px" />
                  </Flex>
                </Tooltip>
              ) : null}

              {checkCollaboratorPermission(validation, currentClient?.guid_client as string, "config.read") ? (
                <Tooltip label="Configurações">
                  <Flex
                    justifyContent="center"
                    gap="8px"
                    p="10px 30px"
                    alignItems="center"
                    _hover={{ cursor: "pointer", backgroundColor: "rgb(255,255,255,0.1)", transition: "300ms" }}
                    onClick={() => requestNavigation("/configuracoes")}
                  >
                    <Settings color="#ffffffd1" width="22px" height="22px" />
                  </Flex>
                </Tooltip>
              ) : null}
            </Flex>
            <Flex direction="column" gap={4} w="100%" mt={4} mb={6} flexShrink={0}>
              <Divider w="100%" />
              <LogoutModal st="simple" />
            </Flex>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}
